import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { S } from '../utils/styles';
import { useAuth } from '../App';

const ACTION_LABEL = {
  create: 'Created', update: 'Updated', delete: 'Deleted',
  close: 'Closed', dispatch: 'Dispatched', login: 'Logged in',
  restore: 'Restored', receipt: 'Received goods',
};

function formatWhen(ts) {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function safeParse(v) {
  if (!v) return null;
  try { return JSON.parse(v); } catch { return v; }
}

function displayVal(v) {
  if (v === null || v === undefined) return '(none)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Builds a readable list of what changed, field by field
function buildDiff(oldRaw, newRaw) {
  const oldObj = safeParse(oldRaw);
  const newObj = safeParse(newRaw);
  if ((oldObj === null || typeof oldObj !== 'object') && (newObj === null || typeof newObj !== 'object')) {
    return [{ key: null, old: oldObj, next: newObj }];
  }
  const keys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);
  return Array.from(keys).map(key => ({
    key, old: oldObj ? oldObj[key] : undefined, next: newObj ? newObj[key] : undefined,
  })).filter(d => JSON.stringify(d.old) !== JSON.stringify(d.next));
}

export default function ActivityLog() {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [signingOut, setSigningOut] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = React.useRef(null);

  useEffect(() => {
    api.get('/inventory/full-activity-log')
      .then(r => setRows(r.data))
      .catch(e => setError(e.response?.data?.error || 'Could not load activity log'))
      .finally(() => setLoading(false));
    api.get('/auth/users').then(r => setAllUsers(r.data)).catch(() => {});
    api.get('/backup/status').then(r => setBackupStatus(r.data)).catch(() => {});
  }, []);

  const forceLogout = async (u) => {
    if (!window.confirm(`Sign out ${u.name} now? They'll be logged out immediately and can log back in right away.`)) return;
    setSigningOut(u.id);
    try {
      await api.post(`/auth/force-logout/${u.id}`);
      alert(`${u.name} has been signed out.`);
    } catch (e) {
      alert(e.response?.data?.error || 'Error signing out user');
    } finally {
      setSigningOut(null);
    }
  };

  const downloadExcelBackup = async () => {
    try {
      const { data } = await api.get('/backup/export-excel', { responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `byldr-backup-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error downloading backup');
    }
  };

  const runBackupNow = async () => {
    setBackingUp(true);
    try {
      const { data } = await api.post('/backup/run-now');
      setBackupStatus(s => ({ ...s, last_backup_at: data.ts, hours_since: 0 }));
      alert(data.emailed ? 'Backup complete and emailed.' : 'Backup saved locally, but email is not set up yet (backend/.env) so it was not emailed.');
    } catch (e) {
      alert(e.response?.data?.error || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  const downloadDbBackup = async () => {
    try {
      const { data } = await api.get('/backup/export-db', { responseType: 'blob' });
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `byldr-backup-${new Date().toISOString().slice(0, 10)}.db`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error downloading backup');
    }
  };

  const pickRestoreFile = () => restoreInputRef.current?.click();

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset, so picking the same file twice still fires this
    if (!file) return;
    const sure = window.confirm(
      `This will REPLACE ALL current data in the system with the contents of "${file.name}".\n\n` +
      `Everything currently in the system will be overwritten — orders, POs, customers, everything.\n\n` +
      `A safety backup of the current state will be taken automatically first, so this can be undone if it's a mistake.\n\n` +
      `Are you sure you want to restore from this file?`
    );
    if (!sure) return;
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/backup/restore', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(data.message || 'Restore complete. Please restart the backend.');
    } catch (e) {
      alert(e.response?.data?.error || 'Restore failed. Nothing was changed.');
    } finally {
      setRestoring(false);
    }
  };

  const users = ['all', ...Array.from(new Set(rows.map(r => r.user_name).filter(Boolean)))];
  const visible = userFilter === 'all' ? rows : rows.filter(r => r.user_name === userFilter);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 19 }}>Activity Log</h2>
        <select value={userFilter} onChange={e => setUserFilter(e.target.value)} style={{ ...S.input, width: 200 }}>
          {users.map(u => <option key={u} value={u}>{u === 'all' ? 'All users' : u}</option>)}
        </select>
      </div>

      {allUsers.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Users</div>
          {allUsers.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span>{u.name} <span style={{ color: '#9ca3af', textTransform: 'capitalize', fontSize: 11 }}>({u.role})</span></span>
              {u.id !== currentUser?.id && (
                <button
                  onClick={() => forceLogout(u)}
                  disabled={signingOut === u.id}
                  style={{ fontSize: 11, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}
                >{signingOut === u.id ? 'Signing out...' : 'Force sign out'}</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Backups</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          {backupStatus?.last_backup_at
            ? `Last automatic backup: ${backupStatus.hours_since < 1 ? 'less than an hour ago' : `${Math.floor(backupStatus.hours_since)}h ago`}`
            : 'No automatic backup has run yet'}
        </div>
        <button onClick={downloadExcelBackup} style={S.btnSecondary}>⬇ Download Excel snapshot now</button>
        <button onClick={downloadDbBackup} style={S.btnSecondary}>⬇ Download restore-ready backup now</button>
        <button onClick={runBackupNow} disabled={backingUp} style={S.btnSecondary}>
          {backingUp ? 'Running backup...' : '💾 Run full backup + email now'}
        </button>
        <div style={{ width: '100%', borderTop: '1px solid #f1f2f5', margin: '4px 0' }} />
        <input ref={restoreInputRef} type="file" accept=".db" style={{ display: 'none' }} onChange={handleRestoreFile} />
        <button
          onClick={pickRestoreFile}
          disabled={restoring}
          style={{ ...S.btnSecondary, color: '#dc2626', borderColor: '#fca5a5' }}
        >{restoring ? 'Restoring...' : '⚠ Restore from a backup file...'}</button>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>Replaces all current data. A safety backup is taken automatically first.</div>
      </div>

      {loading && <div style={{ color: '#9ca3af' }}>Loading...</div>}
      {error && <div style={{ color: '#dc2626' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: '10px 14px' }}>When</th>
                <th style={{ padding: '10px 14px' }}>User</th>
                <th style={{ padding: '10px 14px' }}>Role</th>
                <th style={{ padding: '10px 14px' }}>Action</th>
                <th style={{ padding: '10px 14px' }}>On</th>
                <th style={{ padding: '10px 14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const hasDetail = r.old_value || r.new_value;
                const isOpen = expandedId === r.id;
                const diff = hasDetail ? buildDiff(r.old_value, r.new_value) : [];
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      style={{ borderTop: '1px solid #f1f2f5', cursor: hasDetail ? 'pointer' : 'default' }}
                      onClick={() => hasDetail && setExpandedId(isOpen ? null : r.id)}
                    >
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{formatWhen(r.created_at)}</td>
                      <td style={{ padding: '10px 14px' }}>{r.user_name || '—'}</td>
                      <td style={{ padding: '10px 14px', textTransform: 'capitalize', color: '#9ca3af' }}>{r.user_role || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>{ACTION_LABEL[r.action] || r.action}</td>
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{r.table_name}{r.record_id ? ` #${r.record_id}` : ''}</td>
                      <td style={{ padding: '10px 14px', color: '#3b82f6', fontSize: 12 }}>{hasDetail ? (isOpen ? 'Hide ▲' : 'Details ▼') : ''}</td>
                    </tr>
                    {isOpen && (
                      <tr style={{ background: '#f9fafb' }}>
                        <td colSpan={6} style={{ padding: '10px 14px 14px 14px' }}>
                          {diff.length === 0 && <div style={{ color: '#9ca3af', fontSize: 12 }}>No field-level detail recorded for this action.</div>}
                          {diff.length > 0 && (
                            <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                              <tbody>
                                {diff.map((d, i) => (
                                  <tr key={i}>
                                    <td style={{ padding: '3px 10px 3px 0', color: '#6b7280', fontWeight: 600 }}>{d.key || 'value'}</td>
                                    {d.old !== undefined && d.next !== undefined ? (
                                      <td style={{ padding: '3px 0' }}>
                                        <span style={{ color: '#dc2626' }}>{displayVal(d.old)}</span>
                                        <span style={{ color: '#9ca3af', margin: '0 6px' }}>→</span>
                                        <span style={{ color: '#10b981' }}>{displayVal(d.next)}</span>
                                      </td>
                                    ) : (
                                      <td style={{ padding: '3px 0', color: '#10b981' }}>{displayVal(d.next !== undefined ? d.next : d.old)}</td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No activity recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
